import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useAppSelector } from 'src/app/hooks';
import { selectMap3DState } from 'src/map-3d/shared/map-3d-slices';
import { type CrossSectionPoint } from '../graph-viewer';
import {
  dataComponents,
  slopeLineWidthPx,
  zoomProperties,
} from '../graph-viewer/constants';
import {
  calculateHoverRadius,
  calculateSpriteScale,
  calculateTubeRadius,
  convertCopcToCartesian3,
  createSlopeLineGeometry,
  findClosestPoint,
} from '../graph-viewer/helpers';
import type { UseCrossSectionInteractionsOptions } from './types';

/**
 * Hook to handle interactions with the cross-section profile.
 * @param canvasContainerRef - Ref to the canvas container element.
 * @param data - The data for the cross-section profile.
 * @param d3Refs - Ref to the D3 refs.
 * @param threeJsRefs - Ref to the Three.js refs.
 * @param interactionStateRef - Ref to the interaction state.
 * @param onHoverPoint - Callback to handle when a point is hovered.
 * @param onPointClick - Callback to handle when a point is clicked.
 * @param updateHighlightSprite - Callback to update the highlight sprite.
 * @param hideHighlightSprite - Callback to hide the highlight sprite.
 * @param renderScene - Callback to render the scene.
 * @param updateScalesAndAxes - Callback to update the scales and axes.
 */

export const useCrossSectionInteractions = ({
  canvasContainerRef,
  data,
  d3Refs,
  threeJsRefs,
  interactionStateRef,
  highlightConfig,
  isSlopeToolActive = false,
  slopeToolState,
  onSlopePointClick,
  onHoverPoint,
  onPointClick,
  updateHighlightSprite,
  hideHighlightSprite,
  renderScene,
  updateScalesAndAxes,
}: UseCrossSectionInteractionsOptions) => {
  const { cesiumProxy } = useAppSelector(selectMap3DState);
  const viewer = cesiumProxy?.viewer;

  const updateSlopePoints = useCallback(() => {
    const refs = threeJsRefs.current;
    if (
      isNil(refs) ||
      isNil(refs.slopePointASprite) ||
      isNil(refs.slopePointBSprite) ||
      isNil(refs.slopeLine) ||
      isNil(refs.camera) ||
      isNil(canvasContainerRef.current) ||
      isNil(slopeToolState)
    )
      return;

    const { slopePointASprite, slopePointBSprite, slopeLine, camera } = refs;

    const { pointA, pointB } = slopeToolState;

    if (pointA) {
      slopePointASprite.visible = true;
      slopePointASprite.position.set(pointA.mileage, 0, pointA.elevation);
      const worldSize = calculateSpriteScale(
        camera,
        canvasContainerRef.current.clientHeight,
        18,
      );
      slopePointASprite.scale.set(worldSize, worldSize, 1);
    } else {
      slopePointASprite.visible = false;
    }

    if (pointB) {
      slopePointBSprite.visible = true;
      slopePointBSprite.position.set(pointB.mileage, 0, pointB.elevation);

      const worldSize = calculateSpriteScale(
        camera,
        canvasContainerRef.current.clientHeight,
        18,
      );

      slopePointBSprite.scale.set(worldSize, worldSize, 1);

      if (pointA) {
        const tubeRadius = calculateTubeRadius(
          camera,
          canvasContainerRef.current.clientHeight,
          slopeLineWidthPx,
        );

        const newGeometry = createSlopeLineGeometry(pointA, pointB, tubeRadius);

        if (slopeLine.geometry) {
          slopeLine.geometry.dispose();
        }
        slopeLine.geometry = newGeometry;
        slopeLine.visible = true;
      }
    } else {
      slopePointBSprite.visible = false;
      slopeLine.visible = false;
    }
  }, [slopeToolState, threeJsRefs, canvasContainerRef]);

  const handlePan = useCallback(
    (deltaX: number, deltaY: number) => {
      const threeJsRef = threeJsRefs.current;
      if (isNil(threeJsRef?.camera)) {
        return;
      }

      threeJsRef.camera.position.x -= deltaX;
      threeJsRef.camera.position.z -= deltaY;
    },
    [threeJsRefs],
  );

  const handleZoom = useCallback(
    (zoomIn: boolean, mouseX: number, mouseY: number) => {
      const threeJsRef = threeJsRefs.current;
      const d3Ref = d3Refs.current;
      const interactionState = interactionStateRef.current;

      if (
        isNil(threeJsRef?.camera) ||
        isNil(d3Ref?.scaleX) ||
        isNil(d3Ref?.scaleY) ||
        isNil(interactionState?.scale)
      ) {
        return;
      }

      // Hide highlight sprite during zoom to prevent it from scaling incorrectly.
      hideHighlightSprite();

      const { camera } = threeJsRef;
      const { scaleX: scaleXBefore, scaleY: scaleYBefore } = d3Ref;
      const { scale } = interactionState;

      // Store world position before zoom.
      const worldPosBeforeZoom = [
        scaleXBefore.invert(mouseX),
        scaleYBefore.invert(mouseY),
      ];

      // Update scale.
      scale.multiplyScalar(
        zoomIn ? zoomProperties.inFactor : zoomProperties.outFactor,
      );

      // Update camera bounds and axes.
      updateScalesAndAxes();

      // Get the updated scales after zoom.
      const d3RefAfter = d3Refs.current;
      if (isNil(d3RefAfter?.scaleX) || isNil(d3RefAfter?.scaleY)) {
        return;
      }

      const { scaleX: scaleXAfter, scaleY: scaleYAfter } = d3RefAfter;

      // Calculate world position after zoom.
      const worldPosAfterZoom = [
        scaleXAfter.invert(mouseX),
        scaleYAfter.invert(mouseY),
      ];

      // Adjust camera position to keep mouse point stationary.
      camera.position.x -= worldPosAfterZoom[0] - worldPosBeforeZoom[0];
      camera.position.z -= worldPosAfterZoom[1] - worldPosBeforeZoom[1];

      updateScalesAndAxes();

      // Update slope points if tool is active (camera bounds changed)
      if (isSlopeToolActive) {
        updateSlopePoints();
      }
    },
    [
      d3Refs,
      threeJsRefs,
      interactionStateRef,
      updateScalesAndAxes,
      hideHighlightSprite,
      isSlopeToolActive,
      updateSlopePoints,
    ],
  );

  const findAndHighlightPoint = useCallback(
    (mouseX: number, mouseY: number) => {
      const d3Ref = d3Refs.current;
      if (
        isNil(d3Ref?.scaleX) ||
        isNil(d3Ref?.scaleY) ||
        isNil(data) ||
        data.count === 0
      ) {
        hideHighlightSprite();
        onHoverPoint?.(null);
        return null;
      }

      const { scaleX, scaleY } = d3Ref;

      const mileage = scaleX.invert(mouseX);
      const elevation = scaleY.invert(mouseY);
      const radius = calculateHoverRadius(scaleX);

      const closestPoint = findClosestPoint(data, mileage, elevation, radius);
      const { line, sourceCrs } = highlightConfig;

      if (isNil(viewer)) {
        return;
      }

      if (closestPoint) {
        const { sourcePositionComponents } = dataComponents;
        const dataIndex = closestPoint.index * sourcePositionComponents;
        const pointMileage = data.positions[dataIndex];
        const pointElevation = data.positions[dataIndex + 3];

        updateHighlightSprite(pointMileage, pointElevation);
        onHoverPoint?.(closestPoint.index);

        if (isNil(line) || isNil(sourceCrs)) {
          return;
        }

        const copcX = data.positions[dataIndex + 1];
        const copcY = data.positions[dataIndex + 2];
        const copcZ = data.positions[dataIndex + 3];

        const position = convertCopcToCartesian3(
          copcX,
          copcY,
          copcZ,
          sourceCrs,
        );

        if (position) {
          line.saveHighlightPointAt3DPosition(position);
          viewer.scene.requestRender();
        } else {
          line.removeHighlightPoint();
        }
      } else {
        hideHighlightSprite();
        onHoverPoint?.(null);

        if (line) {
          line.removeHighlightPoint();
          if (viewer) {
            viewer.scene.requestRender();
          }
        }
      }

      return closestPoint;
    },
    [
      data,
      d3Refs,
      highlightConfig,
      viewer,
      onHoverPoint,
      updateHighlightSprite,
      hideHighlightSprite,
    ],
  );

  useEffect(() => {
    const refs = threeJsRefs.current;
    if (isNil(refs)) return;

    if (!isSlopeToolActive) {
      if (refs.slopePointASprite) refs.slopePointASprite.visible = false;
      if (refs.slopePointBSprite) refs.slopePointBSprite.visible = false;
      if (refs.slopeLine) refs.slopeLine.visible = false;
      renderScene();
    } else {
      updateSlopePoints();
      renderScene();
    }
  }, [isSlopeToolActive, slopeToolState, updateSlopePoints, renderScene]);

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (isNil(container)) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (event.button === 0) {
        const interactionState = interactionStateRef.current;
        if (!isNil(interactionState)) {
          interactionState.isMouseDown = true;
        }
      }
    };

    const handleMouseUp = () => {
      const interactionState = interactionStateRef.current;
      if (!isNil(interactionState)) {
        interactionState.isMouseDown = false;
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      const d3Ref = d3Refs.current;
      const interactionState = interactionStateRef.current;

      if (
        isNil(d3Ref?.scaleX) ||
        isNil(d3Ref?.scaleY) ||
        isNil(interactionState)
      ) {
        return;
      }

      const { scaleX, scaleY } = d3Ref;
      const { mouse, isMouseDown } = interactionState;

      const rect = container.getBoundingClientRect();
      const currentMouseX = event.clientX - rect.left;
      const currentMouseY = event.clientY - rect.top;

      if (isMouseDown) {
        // Hide highlight sprite during pan/drag.
        hideHighlightSprite();

        const previousWorldPos = [
          scaleX.invert(mouse.x),
          scaleY.invert(mouse.y),
        ];
        const currentWorldPos = [
          scaleX.invert(currentMouseX),
          scaleY.invert(currentMouseY),
        ];

        const deltaX = currentWorldPos[0] - previousWorldPos[0];
        const deltaY = currentWorldPos[1] - previousWorldPos[1];

        handlePan(deltaX, deltaY);
        updateScalesAndAxes();
        if (isSlopeToolActive) {
          updateSlopePoints();
        }
        renderScene();
      } else if (!isSlopeToolActive) {
        findAndHighlightPoint(currentMouseX, currentMouseY);
        renderScene();
      } else {
        hideHighlightSprite();
        onHoverPoint?.(null);
        renderScene();
      }

      mouse.set(currentMouseX, currentMouseY);
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const interactionState = interactionStateRef.current;
      if (isNil(interactionState)) {
        return;
      }

      // Hide highlight sprite immediately when zoom starts.
      hideHighlightSprite();

      const { mouse } = interactionState;
      const isZoomIn = event.deltaY < 0;

      handleZoom(isZoomIn, mouse.x, mouse.y);

      // Update slope points if tool is active
      if (isSlopeToolActive) {
        updateSlopePoints();
      }

      // Render scene after zoom completes.
      renderScene();
    };

    const handleClick = (event: MouseEvent) => {
      const d3Ref = d3Refs.current;

      if (
        isNil(d3Ref?.scaleX) ||
        isNil(d3Ref?.scaleY) ||
        isEmpty(data) ||
        isNil(onPointClick)
      ) {
        return;
      }

      const { scaleX, scaleY } = d3Ref;

      const rect = container.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const mileage = scaleX.invert(mouseX);
      const elevation = scaleY.invert(mouseY);
      const radius = calculateHoverRadius(scaleX);

      const closestPoint = findClosestPoint(data, mileage, elevation, radius);

      if (closestPoint) {
        const { sourcePositionComponents } = dataComponents;
        const dataIndex = closestPoint.index * sourcePositionComponents;
        const pointMileage = data.positions[dataIndex];
        const pointElevation = data.positions[dataIndex + 3];

        const point: CrossSectionPoint = {
          index: closestPoint.index,
          mileage: pointMileage,
          elevation: pointElevation,
        };

        if (isSlopeToolActive && onSlopePointClick) {
          onSlopePointClick(point);
        } else if (onPointClick) {
          onPointClick(point);
        }
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('click', handleClick);
    };
  }, [
    data,
    d3Refs,
    isSlopeToolActive,
    slopeToolState,
    onSlopePointClick,
    onPointClick,
    handlePan,
    handleZoom,
    findAndHighlightPoint,
    hideHighlightSprite,
    onHoverPoint,
    updateSlopePoints,
    renderScene,
    updateScalesAndAxes,
    canvasContainerRef,
    interactionStateRef,
    hideHighlightSprite,
  ]);

  return {
    updateSlopePoints,
  };
};
